import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, IconButton, Dialog, DialogContent } from '@mui/material';
import { Close, ZoomIn, ImageNotSupported } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

export interface LightboxSection {
    title: string;
    images: string[];
}

// ── SafeThumbBox — a 52×52 thumbnail that disappears if image fails ──
const SafeThumbBox: React.FC<{
    url: string;
    label: string;
    index: number;
    selected: boolean;
    onSelect: () => void;
}> = ({ url, label, index, selected, onSelect }) => {
    const [broken, setBroken] = useState(false);
    if (broken) return null;
    return (
        <Box
            component={motion.div}
            whileHover={{ scale: 1.05 }}
            onClick={onSelect}
            sx={{
                width: 52, height: 52, borderRadius: 1, overflow: 'hidden', cursor: 'pointer',
                border: selected ? '2px solid #b8451e' : '1px solid #ECECE4',
                flexShrink: 0,
            }}
        >
            <img
                src={url}
                alt={`${label} ${index + 1}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                onError={() => setBroken(true)}
            />
        </Box>
    );
};

// ── Thumbnail — clickable, shows a "+N" badge when more images exist behind it ──
export const ImageThumb: React.FC<{
    images: string[] | undefined | null;
    label: string;
    size?: number;
    onClick: (e?: React.MouseEvent) => void;
}> = ({ images, label, size = 36, onClick }) => {
    const safeImages = (images || []).filter(u => typeof u === 'string' && u.startsWith('http'));
    const [firstBroken, setFirstBroken] = useState(false);
    const first = firstBroken ? undefined : safeImages[0];
    const extra = safeImages.length - 1;

    // Reset broken state if images change
    const firstImage = safeImages[0];
    useEffect(() => { setFirstBroken(false); }, [firstImage]);

    const hasImage = !!first;
    return (
        <Box
            onClick={hasImage ? onClick : undefined}
            sx={{
                position: 'relative',
                width: size,
                height: size,
                borderRadius: 1,
                overflow: 'hidden',
                cursor: hasImage ? 'pointer' : 'default',
                bgcolor: '#FAFAF7',
                border: hasImage ? '1px solid #ECECE4' : '1px dashed #DAD6CC',
                flexShrink: 0,
                '&:hover .thumb-overlay': { opacity: hasImage ? 1 : 0 },
            }}
        >
            {first ? (
                <img
                    src={first}
                    alt={label}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    onError={() => setFirstBroken(true)}
                />
            ) : (
                <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ImageNotSupported sx={{ fontSize: size * 0.45, color: '#DAD6CC' }} />
                </Box>
            )}
            {first && (
                <Box
                    className="thumb-overlay"
                    sx={{
                        position: 'absolute', inset: 0, bgcolor: 'rgba(26, 20, 15,0.45)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        opacity: 0, transition: 'opacity 150ms ease-out',
                    }}
                >
                    <ZoomIn sx={{ fontSize: size * 0.5, color: '#fff' }} />
                </Box>
            )}
            {extra > 0 && (
                <Box sx={{
                    position: 'absolute', bottom: 0, right: 0, bgcolor: 'rgba(220, 88, 41,0.92)', color: '#fff',
                    fontSize: '0.55rem', fontWeight: 700, lineHeight: 1, px: 0.4, py: 0.2, borderTopLeftRadius: 4,
                }}>
                    +{extra}
                </Box>
            )}
        </Box>
    );
};

// ── Lightbox — all images across one or more sections, with a large crossfading
// preview and a clickable thumbnail strip grouped by section ──
export const ImageLightbox: React.FC<{
    open: boolean;
    onClose: () => void;
    sections: LightboxSection[];
}> = ({ open, onClose, sections }) => {
    const flatImages = useMemo(() => sections.flatMap(s => s.images.map(url => ({ url, section: s.title }))), [sections]);
    const [selected, setSelected] = useState<string | null>(null);
    const [mainBroken, setMainBroken] = useState(false);

    useEffect(() => {
        if (open) {
            setSelected(flatImages[0]?.url || null);
            setMainBroken(false);
        }
    }, [open, flatImages]);

    const handleSelect = (url: string) => {
        setSelected(url);
        setMainBroken(false);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.05rem' }}>Product images</Typography>
                    <IconButton size="small" onClick={onClose}><Close sx={{ fontSize: 18 }} /></IconButton>
                </Box>

                <Box sx={{
                    position: 'relative', width: '100%', aspectRatio: '1 / 1', bgcolor: '#FAFAF7',
                    borderRadius: 2, overflow: 'hidden', mb: 2, border: '1px solid #ECECE4',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    {selected && !mainBroken ? (
                        <AnimatePresence mode="wait">
                            <motion.img
                                key={selected}
                                src={selected}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.18, ease: 'easeOut' }}
                                style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'absolute', inset: 0 }}
                                onError={() => setMainBroken(true)}
                            />
                        </AnimatePresence>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, color: '#000000' }}>
                            <ImageNotSupported sx={{ fontSize: 48 }} />
                            <Typography variant="caption">Image not available</Typography>
                        </Box>
                    )}
                </Box>

                {sections.map(section => section.images.length > 0 && (
                    <Box key={section.title} sx={{ mb: 1.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#6B625B', textTransform: 'uppercase', letterSpacing: 0.05, display: 'block', mb: 0.75 }}>
                            {section.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {section.images.map((url, i) => (
                                <SafeThumbBox
                                    key={url}
                                    url={url}
                                    label={section.title}
                                    index={i}
                                    selected={selected === url}
                                    onSelect={() => handleSelect(url)}
                                />
                            ))}
                        </Box>
                    </Box>
                ))}
            </DialogContent>
        </Dialog>
    );
};
